import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { 
  Snackbar, 
  Alert, 
  AlertTitle, 
  Button, 
  Typography, 
  Box, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';

// Create context to expose popup status and methods to the app
interface PopupContextType {
  isPopupBlocked: boolean;
  checkPopupBlocked: () => Promise<boolean>;
  resetPopupWarning: () => void;
}

const PopupContext = createContext<PopupContextType>({
  isPopupBlocked: false,
  checkPopupBlocked: async () => false,
  resetPopupWarning: () => {}
});

// Export the context for use in other components
export const usePopupBlocker = () => useContext(PopupContext);

interface PopupBlockerDetectorProps {
  children: ReactNode;
  onDetectBlocked?: (isBlocked: boolean) => void;
  checkOnMount?: boolean;
  showWarning?: boolean;
  persistWarningKey?: string;
}

export const PopupBlockerDetector: React.FC<PopupBlockerDetectorProps> = ({
  children,
  onDetectBlocked,
  checkOnMount = true,
  showWarning = true,
  persistWarningKey = 'popup-warning-dismissed'
}) => {
  const [isPopupBlocked, setIsPopupBlocked] = useState<boolean>(false);
  const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  
  // Check if the warning has been previously dismissed
  const hasBeenDismissed = (): boolean => {
    try {
      return localStorage.getItem(persistWarningKey) === 'true';
    } catch (e) {
      return false;
    }
  };

  // Check if popups are blocked using a small test window
  const checkPopupBlocked = async (): Promise<boolean> => {
    try {
      // Try to open a small test popup
      const testPopup = window.open('about:blank', '_blank', 'width=1,height=1');
      
      // If popup was blocked, testPopup will be null or undefined
      const isBlocked = !testPopup;
      
      // Close the test popup if it opened
      if (testPopup) {
        testPopup.close();
      }
      
      // Update state if popups are blocked
      if (isBlocked !== isPopupBlocked) {
        setIsPopupBlocked(isBlocked);
        
        // Call the callback if provided
        if (onDetectBlocked) {
          onDetectBlocked(isBlocked);
        }
        
        // Show warnings if popups are blocked
        if (isBlocked && showWarning && !hasBeenDismissed()) {
          setShowSnackbar(true);
        }
      }
      
      return isBlocked;
    } catch (e) {
      // Any exception likely means popups are blocked
      console.error("Error checking popup status:", e);
      setIsPopupBlocked(true);
      
      if (onDetectBlocked) {
        onDetectBlocked(true);
      }
      
      if (showWarning && !hasBeenDismissed()) {
        setShowSnackbar(true);
      }
      
      return true;
    }
  };
  
  // Reset warning state
  const resetPopupWarning = () => {
    try {
      localStorage.removeItem(persistWarningKey);
      setShowSnackbar(true);
    } catch (e) {
      console.error("Error resetting popup warning:", e);
    }
  };
  
  // Handle dismissing the warning permanently
  const handleDismissWarning = () => {
    try {
      localStorage.setItem(persistWarningKey, 'true');
    } catch (e) {
      console.error("Error saving dismissal state:", e);
    }
    setShowSnackbar(false);
    setShowDialog(false);
  };
  
  // Check for popup blocking on component mount
  useEffect(() => {
    if (checkOnMount) {
      // Slight delay to ensure the component is fully mounted
      const timer = setTimeout(() => {
        checkPopupBlocked();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [checkOnMount]);
  
  return (
    <PopupContext.Provider value={{ 
      isPopupBlocked, 
      checkPopupBlocked,
      resetPopupWarning
    }}>
      {children}
      
      {/* Snackbar notification for popup blocking */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={10000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="warning" 
          variant="filled"
          onClose={() => setShowSnackbar(false)}
          sx={{ width: '100%', boxShadow: 3 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => setShowDialog(true)}
            >
              Details
            </Button>
          }
        >
          <AlertTitle>Popups are blocked</AlertTitle>
          <Typography variant="body2">
            Your browser is blocking popups, which may affect authentication.
          </Typography>
        </Alert>
      </Snackbar>
      
      {/* Detailed help dialog */}
      <Dialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        aria-labelledby="popup-dialog-title"
      >
        <DialogTitle id="popup-dialog-title" sx={{ 
          display: 'flex', 
          alignItems: 'center',
          backgroundColor: '#ffecb3',
          color: '#663c00'
        }}>
          <WarningIcon sx={{ mr: 1 }} />
          Popups are blocked by your browser
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" paragraph>
              This application uses popups for authentication with Microsoft. Please allow popups for this site to ensure all features work correctly.
            </Typography>
            
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
              How to enable popups:
            </Typography>
            
            <Typography variant="body2" component="div">
              <ol style={{ paddingLeft: '1.5rem', margin: '0.5rem 0' }}>
                <li>Look for the popup blocked icon <Box component="span" sx={{ fontWeight: 'bold' }}>⊘</Box> in your browser's address bar</li>
                <li>Click on it and select "Always allow popups from this site"</li>
                <li>Refresh the page to apply the changes</li>
              </ol>
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mt: 2,
              p: 2,
              bgcolor: 'rgba(255, 229, 204, 0.3)',
              border: '1px solid #ffe0b2',
              borderRadius: 1
            }}>
              <Typography variant="body2">
                After allowing popups, click the refresh button below to reload the page.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={handleDismissWarning}
            color="inherit"
            size="small"
          >
            Don't show again
          </Button>
          <Button 
            variant="outlined"
            size="small"
            onClick={() => setShowDialog(false)}
          >
            Close
          </Button>
          <Button 
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
            sx={{ 
              ml: 1,
              backgroundColor: "#ff6b00",
              '&:hover': {
                backgroundColor: "#e65c00",
              }
            }}
          >
            Refresh Page
          </Button>
        </DialogActions>
      </Dialog>
    </PopupContext.Provider>
  );
};

export default PopupBlockerDetector;